import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import Typography from 'Components/UI/Typography';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import { currency } from 'Helpers/Formats';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import ItemCard from 'Components/ComponentLibrary/Components/Uncategorized/ResourceSummaryCard/ItemCard';

const styles = theme => ({
  root: {},
  navigation: {},
  moduleName: {
    margin: theme.spacing(0, 0, 8, 0),
  },
  subtitle: {
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
  },
  details: {
    marginBottom: theme.spacing(4),
    '& > *': {
      marginBottom: theme.spacing(2.5),
      '&:last-child': {
        marginBottom: theme.spacing(0),
      },
    },
    textAlign: 'center',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  bodyText: {
    fontWeight: theme.typography.fontWeightBold,
  },
  accountCard: {
    margin: 'auto',
    maxWidth: 400,
  },
  hr: {
    maxWidth: theme.spacing(15),
  },
  form: {
    margin: 'auto',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    maxWidth: theme.dimensions.md,
  },
});

const Layout = ({
  className,
  classes,
  index,
  total,
}) => {
  const { pageContentResource } = usePageContentContext();
  const { resource } = useMetaLayoutContext();
  const isNonInheritedIra = resource.attr('isNonInheritedIra') === 'true';
  const accountHasOwner = resource.attr('accountHasOwner') === 'true';
  const isNonInheritedIraAndAccountHasOwner = isNonInheritedIra && accountHasOwner;
  return (
    <Box className={classNames(className, classes.root)}>
      <Box className={classes.moduleName}>
        <Typography
          className={classes.subtitle}
          variant={'h6'}
          color={'black'}
          test={'wizard-module-name'}
        >
          {pageContentResource.attr('moduleName')}
        </Typography>
      </Box>
      <Box className={classes.details}>
        <Typography
          className={classes.title}
          variant={'h2'}
          color={'black'}
          test={'wizard-step-title'}
        >
          {pageContentResource.attr('title')}
        </Typography>
        <Typography
          className={classes.subtitle}
          variant={'h6'}
          color={'primaryGray'}
        >
          {`${index} of ${total}`}
        </Typography>
        <Subtitle
          className={classes.bodyText}
          text={'Please review the investment objectives and time horizons of this account'}
        />
        <Subtitle
          className={classes.bodyText}
          text={'You can only edit accounts for whom you are the account owner.'}
        />
      </Box>
      <ItemCard
        className={classes.accountCard}
        imageURL={resource.attr('image')}
        title={resource.attr('accountName')}
        titleTooltip={resource.attr('accountName')}
        subtitle={currency(parseFloat(resource.attr('accountBalance')))}
        subtitleExtra={resource.attr('accountNumber')}
      />
      <hr className={classes.hr} />
      <Box className={classes.form}>
        <MetaField attr={'objective'} disabled={isNonInheritedIra} />
        <MetaField attr={'timeHorizon'} disabled={isNonInheritedIraAndAccountHasOwner} helperText={isNonInheritedIraAndAccountHasOwner ? resource.attr('timeHorizonHelperText') : null} />
      </Box>
    </Box>
  );
};

Layout.defaultProps = {
  className: '',
};

Layout.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  /** index: The index of the current resource in the collection. One based numbering (arrayIndex+1). Provied by withCollectionStepper HOC * */
  index: PropTypes.number.isRequired,
  /** total: The total resources in the collection. Provided by withCollectionStepper HOC * */
  total: PropTypes.number.isRequired,
};

export default withStyles(styles)(Layout);
